import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { LOGIN } from "@/constants/testIds";

const LOGO_ROUND =
    "https://customer-assets-jt897jd0.emergentagent.net/job_18a53554-3c4c-4394-97e9-2d39c51c5281/artifacts/4eg9cvl3_232787.jpg";

const formatDetail = (d) => {
    if (d == null) return "Something went wrong. Please try again.";
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
    if (d?.msg) return d.msg;
    return String(d);
};

const Login = () => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setBusy(true);
        try {
            await login(email, password);
            navigate("/admin");
        } catch (err) {
            setError(formatDetail(err?.response?.data?.detail) || "Login failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid={LOGIN.root} className="min-h-screen flex items-center justify-center px-6 py-16" style={{ background: "var(--bg-primary)" }}>
            <div className="w-full max-w-md">
                <Link to="/" className="flex items-center gap-3 mb-10">
                    <span className="block w-12 h-12 rounded-full overflow-hidden">
                        <img src={LOGO_ROUND} alt="Piñatas con Karina" className="w-full h-full object-cover" />
                    </span>
                    <span className="font-serif text-xl">
                        Piñatas <em className="font-hand text-2xl" style={{ color: "var(--pink)" }}>con Karina</em>
                    </span>
                </Link>

                <h1 className="font-serif text-5xl md:text-6xl leading-none tracking-tight mb-2" style={{ color: "var(--ink-1)" }}>
                    Studio access
                </h1>
                <p className="font-body text-sm mb-10" style={{ color: "var(--ink-mute)" }}>
                    Sign in to edit the site.
                </p>

                <form onSubmit={submit} className="space-y-6">
                    <label className="block">
                        <span className="font-body text-[10px] uppercase tracking-[0.28em]" style={{ color: "var(--ink-mute)" }}>Email</span>
                        <input
                            type="email"
                            required
                            value={email}
                            data-testid={LOGIN.email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="mt-2 block w-full bg-transparent border-b py-2 font-serif text-xl focus:outline-none focus:border-[var(--pink)] transition-colors"
                            style={{ color: "var(--ink-1)", borderColor: "rgba(46,16,37,0.25)" }}
                        />
                    </label>
                    <label className="block">
                        <span className="font-body text-[10px] uppercase tracking-[0.28em]" style={{ color: "var(--ink-mute)" }}>Password</span>
                        <input
                            type="password"
                            required
                            value={password}
                            data-testid={LOGIN.password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="mt-2 block w-full bg-transparent border-b py-2 font-serif text-xl focus:outline-none focus:border-[var(--pink)] transition-colors"
                            style={{ color: "var(--ink-1)", borderColor: "rgba(46,16,37,0.25)" }}
                        />
                    </label>

                    {error && (
                        <div data-testid={LOGIN.error} className="font-body text-sm p-3 rounded-md" style={{ background: "#FFE1EA", color: "#8A0E39" }}>
                            {error}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={busy}
                        data-testid={LOGIN.submit}
                        className="btn-pink w-full px-6 py-4 rounded-full text-sm font-semibold tracking-wide inline-flex items-center justify-center disabled:opacity-60"
                    >
                        {busy ? "Signing in…" : "Sign in →"}
                    </button>
                </form>

                <p className="mt-10 text-xs font-body" style={{ color: "var(--ink-mute)" }}>
                    Contact the studio to request access.
                </p>
            </div>
        </div>
    );
};

export default Login;
